import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

/* ── Tokens ─────────────────────────────────────────────────────────────── */
const T = {
  navy:    "#1E4D8F",
  gold:    "#D4AF37",
  white:   "#ffffff",
  bg:      "#f7f5f1",
  slate:   "#5c6b7e",
  slateLt: "#9ca8b7",
  line:    "#eeebe6",
  peach:   "#ffebd3",
  inputBg: "#f8f7f5",
} as const;

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad2 = (n: number) => String(n).padStart(2, "0");

/* ── Props ──────────────────────────────────────────────────────────────── */
interface Props {
  value: string;                     // "YYYY-MM-DDTHH:mm" or ""
  onChange: (value: string) => void;
  label?: string;
  error?: boolean;
  size?: "small" | "medium";
  sx?: object;
  minDate?: string;                  // "YYYY-MM-DD"
  placeholder?: string;
}

/* ── Component ──────────────────────────────────────────────────────────── */
const PremiumDateTimePicker: React.FC<Props> = ({
  value, onChange, label = "Date & Time", error = false,
  size = "small", sx = {}, minDate, placeholder = "Select date & time",
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  // Parse value into date part and time part
  const { datePart, timePart } = useMemo(() => {
    if (!value) return { datePart: "", timePart: "12:00" };
    const [d, t] = value.split("T");
    return { datePart: d || "", timePart: t?.slice(0, 5) || "12:00" };
  }, [value]);

  const [hours, minutes] = timePart.split(":").map(Number);

  const parsed = useMemo(() => {
    if (datePart) {
      const [y, m] = datePart.split("-").map(Number);
      return { year: y, month: m - 1 };
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  }, [datePart]);

  const [viewYear, setViewYear] = useState(parsed.year);
  const [viewMonth, setViewMonth] = useState(parsed.month);
  const [hovered, setHovered] = useState<string | null>(null);

  React.useEffect(() => {
    if (datePart) {
      const [y, m] = datePart.split("-").map(Number);
      setViewYear(y);
      setViewMonth(m - 1);
    }
  }, [datePart]);

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }, []);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const firstDow = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const monthLabel = `${MONTHS[viewMonth]} ${viewYear}`;

  const isDisabled = useCallback((dateStr: string) => {
    if (!minDate) return false;
    return dateStr < minDate;
  }, [minDate]);

  const emitChange = (newDate: string, newTime: string) => {
    if (!newDate) return;
    onChange(`${newDate}T${newTime}`);
  };

  const handleSelect = (day: number) => {
    const dateStr = `${viewYear}-${pad2(viewMonth + 1)}-${pad2(day)}`;
    if (isDisabled(dateStr)) return;
    emitChange(dateStr, timePart);
  };

  const handleTimeChange = (newHours: number, newMinutes: number) => {
    const t = `${pad2(newHours)}:${pad2(newMinutes)}`;
    const d = datePart || today;
    emitChange(d, t);
  };

  const handleToday = () => {
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    emitChange(today, timePart);
  };

  const handleClear = () => {
    onChange("");
    setOpen(false);
  };

  const handleDone = () => setOpen(false);

  // Display value
  const displayValue = useMemo(() => {
    if (!value || !datePart) return "";
    const d = new Date(datePart + "T12:00:00");
    const dateStr = d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    const h = hours % 12 || 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    return `${dateStr} at ${h}:${pad2(minutes)} ${ampm}`;
  }, [value, datePart, hours, minutes]);

  const HOUR_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1–12
  const MINUTE_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  const isPM = hours >= 12;
  const display12 = hours % 12 || 12;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: "relative" }}>
        <TextField
          label={label}
          fullWidth
          size={size}
          value={displayValue}
          placeholder={placeholder}
          onClick={() => setOpen(o => !o)}
          error={error}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <CalendarTodayOutlinedIcon sx={{ fontSize: 17, color: T.slateLt, cursor: "pointer" }} />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: !!displayValue || open }}
          sx={{
            ...sx,
            cursor: "pointer",
            "& .MuiInputBase-input": { cursor: "pointer" },
          }}
        />

        <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" style={{ zIndex: 1500 }}>
          <Box sx={{
            mt: 1, bgcolor: T.white, borderRadius: "14px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 12px 40px rgba(0,0,0,0.04)",
            overflow: "hidden", width: 310, userSelect: "none",
          }}>
            {/* ── Month header ── */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5 }}>
              <IconButton size="small" onClick={prevMonth}
                sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: T.bg, "&:hover": { bgcolor: T.peach } }}>
                <ChevronLeftIcon sx={{ fontSize: 18, color: T.navy }} />
              </IconButton>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy }}>{monthLabel}</Typography>
              <IconButton size="small" onClick={nextMonth}
                sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: T.bg, "&:hover": { bgcolor: T.peach } }}>
                <ChevronRightIcon sx={{ fontSize: 18, color: T.navy }} />
              </IconButton>
            </Box>

            {/* ── Weekday headers ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", px: 1.5, pb: 0.5 }}>
              {DOW.map(d => (
                <Box key={d} sx={{ textAlign: "center", py: 0.5 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {d}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* ── Date grid ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", px: 1.5, pb: 1.5, gap: "2px" }}>
              {Array.from({ length: firstDow }).map((_, i) => (
                <Box key={`e-${i}`} sx={{ width: 38, height: 38 }} />
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const dateStr = `${viewYear}-${pad2(viewMonth + 1)}-${pad2(day)}`;
                const isSelected = dateStr === datePart;
                const isToday = dateStr === today;
                const disabled = isDisabled(dateStr);
                const isHov = hovered === dateStr && !disabled && !isSelected;
                return (
                  <Box
                    key={day}
                    onClick={() => !disabled && handleSelect(day)}
                    onMouseEnter={() => setHovered(dateStr)}
                    onMouseLeave={() => setHovered(null)}
                    sx={{
                      width: 38, height: 38, borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: disabled ? "default" : "pointer",
                      opacity: disabled ? 0.3 : 1,
                      transition: "all 0.15s ease",
                      bgcolor: isSelected ? T.navy : isHov ? T.peach : isToday ? T.bg : "transparent",
                      boxShadow: isSelected ? "0 2px 8px rgba(13,27,42,0.2)" : "none",
                      border: isToday && !isSelected ? `1.5px solid ${T.gold}` : "1.5px solid transparent",
                      "&:hover": !disabled && !isSelected ? { bgcolor: T.peach, transform: "scale(1.05)" } : {},
                    }}
                  >
                    <Typography sx={{
                      fontSize: 13, fontWeight: isSelected || isToday ? 700 : 500,
                      color: isSelected ? T.white : isToday ? T.gold : T.navy, lineHeight: 1,
                    }}>
                      {day}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* ── Time picker (dropdowns) ── */}
            <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${T.line}`, bgcolor: T.bg }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.2 }}>
                <AccessTimeIcon sx={{ fontSize: 14, color: T.gold }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.navy }}>Time</Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {/* Hour dropdown */}
                <Box sx={{
                  flex: 1, bgcolor: T.white, borderRadius: "8px", border: `1px solid ${T.line}`,
                  overflow: "hidden",
                }}>
                  <select
                    value={display12}
                    onChange={(e) => {
                      let h = parseInt(e.target.value);
                      if (isPM && h !== 12) h += 12;
                      if (!isPM && h === 12) h = 0;
                      handleTimeChange(h, minutes);
                    }}
                    style={{
                      width: "100%", padding: "8px 10px", border: "none", outline: "none",
                      fontSize: 13, fontWeight: 600, color: T.navy, background: "transparent",
                      cursor: "pointer", appearance: "auto",
                    }}
                  >
                    {HOUR_12.map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </Box>

                <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.navy }}>:</Typography>

                {/* Minute dropdown */}
                <Box sx={{
                  flex: 1, bgcolor: T.white, borderRadius: "8px", border: `1px solid ${T.line}`,
                  overflow: "hidden",
                }}>
                  <select
                    value={minutes}
                    onChange={(e) => handleTimeChange(hours, parseInt(e.target.value))}
                    style={{
                      width: "100%", padding: "8px 10px", border: "none", outline: "none",
                      fontSize: 13, fontWeight: 600, color: T.navy, background: "transparent",
                      cursor: "pointer", appearance: "auto",
                    }}
                  >
                    {MINUTE_OPTIONS.map(m => (
                      <option key={m} value={m}>{pad2(m)}</option>
                    ))}
                  </select>
                </Box>

                {/* AM/PM toggle */}
                <Box sx={{ display: "flex", borderRadius: "8px", border: `1px solid ${T.line}`, overflow: "hidden" }}>
                  {["AM", "PM"].map(period => {
                    const isActive = (period === "AM" && !isPM) || (period === "PM" && isPM);
                    return (
                      <Box
                        key={period}
                        onClick={() => {
                          const newH = period === "AM"
                            ? (hours >= 12 ? hours - 12 : hours)
                            : (hours < 12 ? hours + 12 : hours);
                          handleTimeChange(newH, minutes);
                        }}
                        sx={{
                          px: 1.5, py: 1, cursor: "pointer",
                          bgcolor: isActive ? T.navy : T.white,
                          transition: "all 0.15s",
                          "&:hover": { bgcolor: isActive ? T.navy : T.peach },
                        }}
                      >
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: isActive ? T.white : T.slate }}>
                          {period}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* ── Footer ── */}
            <Box sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              px: 2, py: 1.2, borderTop: `1px solid ${T.line}`,
            }}>
              <Typography onClick={handleToday}
                sx={{ fontSize: 12, fontWeight: 600, color: T.gold, cursor: "pointer", "&:hover": { color: T.navy }, transition: "color 0.15s" }}>
                Now
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Typography onClick={handleClear}
                  sx={{ fontSize: 12, fontWeight: 500, color: T.slateLt, cursor: "pointer", "&:hover": { color: T.navy }, transition: "color 0.15s" }}>
                  Clear
                </Typography>
                <Typography onClick={handleDone}
                  sx={{ fontSize: 12, fontWeight: 700, color: T.navy, cursor: "pointer", "&:hover": { color: T.gold }, transition: "color 0.15s" }}>
                  Done
                </Typography>
              </Box>
            </Box>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default PremiumDateTimePicker;
